import type { PerformanceEventProperties } from '@ms/yammer-telemetry';
import type { PageLoadPerformanceEventProperties } from '@ms/yammer-telemetry-support';
import type { Emitter } from 'mitt';
import mitt from 'mitt';

export type PageLoadEventName = 'Loaded' | 'TimedOut' | 'Failed' | 'FailedRecoverable' | 'Interrupted';
export type RouteChangedEventName = 'RouteChanged';
export type PageLoadEventData = PerformanceEventProperties<PageLoadPerformanceEventProperties>;

type LoadEvents = Record<PageLoadEventName, PageLoadEventData>;
type RouteChangedEvents = Record<RouteChangedEventName, void>;
type PageLoadEvents = LoadEvents & RouteChangedEvents;

let emitter: Emitter<PageLoadEvents>;

type SubscribeToPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  callback: (data: PageLoadEvents[E]) => void
) => void;
export const subscribeToPageLoadEvent: SubscribeToPageLoadEvent = (event, callback) => {
  if (!emitter) {
    emitter = mitt<PageLoadEvents>();
  }
  emitter.on(event, callback);
};

type UnsubscribeFromPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  callback: (data: PageLoadEvents[E]) => void
) => void;
export const unsubscribeFromPageLoadEvent: UnsubscribeFromPageLoadEvent = (event, callback) => {
  if (!emitter) {
    return;
  }

  emitter.off(event, callback);
};

/**
 * @internal
 * Emits a page load lifecycle event. This should be considered a private API and should
 * not be called outside of the page-route-telemetry package except for test scenarios.
 */
export const emitPageLoadEvent = (
  ...[event, data]: [event: PageLoadEventName, data: PageLoadEventData] | [event: RouteChangedEventName]
): void => {
  if (!emitter) {
    return;
  }

  emitter.emit(event, data);
};
